import React, { useState, useRef, useCallback } from 'react';
import { Stage, Layer, Line, Rect, RegularPolygon, Text, Group } from 'react-konva';
import { DrawingData, DrawingTool } from '../types';

// Convert MT5 BGR color to RGB hex
const convertMT5Color = (mt5Color: number): string => {
  // MT5 stores colors as BGR (Blue-Green-Red) integer
  const blue = (mt5Color >> 16) & 0xFF;
  const green = (mt5Color >> 8) & 0xFF;
  const red = mt5Color & 0xFF;
  
  // Convert to RGB hex
  const rgb = (red << 16) | (green << 8) | blue;
  const hexColor = `#${rgb.toString(16).padStart(6, '0')}`;
  
  // Debug logging for color conversion issues - always log for HLINE colors
  if (mt5Color === 32768 || mt5Color === 255) {
    console.log(`🎨 Color conversion: MT5=${mt5Color} (0x${mt5Color.toString(16)}) -> BGR(${blue},${green},${red}) -> RGB(${red},${green},${blue}) -> ${hexColor}`);
  }
  
  return hexColor;
};

interface DrawingOverlayProps {
  width: number;
  height: number;
  drawings: DrawingData | null;
  selectedTool: DrawingTool;
  getChartCoordinates: (time: number, price: number) => { x: number; y: number } | null;
  getChartPrice: (x: number, y: number) => { time: number; price: number } | null;
  getPriceScaleWidth: () => number;
  style?: React.CSSProperties;
}

interface LocalDrawing {
  id: string;
  type: DrawingTool;
  points: number[];
  color: string;
  strokeWidth: number;
  isFinished: boolean;
}

const DrawingOverlay: React.FC<DrawingOverlayProps> = ({
  width,
  height,
  drawings,
  selectedTool,
  getChartCoordinates,
  getChartPrice,
  getPriceScaleWidth,
  style
}) => {
  const [localDrawings, setLocalDrawings] = useState<LocalDrawing[]>([]);
  const [currentDrawing, setCurrentDrawing] = useState<LocalDrawing | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hoveredArrow, setHoveredArrow] = useState<string | null>(null);
  const stageRef = useRef<any>(null);

  // Debug: Log local drawings when they change
  React.useEffect(() => {
    if (localDrawings.length > 0) {
      console.log(`🚨 LOCAL DRAWINGS DETECTED: ${localDrawings.length} user-drawn objects`);
      localDrawings.forEach((drawing, i) => {
        console.log(`   Local Drawing ${i}: type=${drawing.type}, id=${drawing.id}, finished=${drawing.isFinished}`);
      });
      console.log(`⚠️  These local drawings may be causing extra horizontal lines! Check if user has drawn lines using drawing tools.`);
    }
  }, [localDrawings]);

  // Clear local drawings when server drawings change (new session)
  React.useEffect(() => {
    if (drawings?.drawings) {
      console.log(`🔄 New server drawings received, clearing ${localDrawings.length} local drawings`);
      setLocalDrawings([]);
      setCurrentDrawing(null);
      setIsDrawing(false);
    }
  }, [drawings?.drawings?.length]); // Only trigger when number of drawings changes

  // Convert server drawings to display coordinates
  const convertedDrawings = React.useMemo(() => {
    if (!drawings?.drawings) return [];

    const timestamp = Date.now();
    console.log(`🔄 [${timestamp}] Converting ${drawings.drawings.length} drawings, HLINEs: ${drawings.drawings.filter(d => d.type === 'HLINE').map(d => `${d.name}(${d.color})`).join(', ')}`);
    
    // Remove zoom detection - just position text correctly
    
    const results = drawings.drawings.map(drawing => {
      // For horizontal lines, we only need the Y coordinate from price
      if (drawing.type === 'HLINE') {
        // Try to get a valid time from the first available chart bar
        let validTime = Date.now() / 1000; // fallback to current timestamp
        
        // Try to use a time from another drawing object or from chart data
        const otherDrawing = drawings.drawings.find(d => d.type !== 'HLINE' && d.time1 > 0);
        if (otherDrawing) {
          validTime = otherDrawing.time1;
        }
        
        const yCoord = getChartCoordinates(validTime, drawing.price1);
        
        // DEBUG: Check if HLINE coordinate calls are creating extra line artifacts
        console.log(`🟢 HLINE "${drawing.name}" coordinate call: validTime=${validTime}, price=${drawing.price1}`);
        if (!yCoord) {
          console.log(`❌ HLINE "${drawing.name}" at price ${drawing.price1} -> NO COORDINATES (out of range)`);
          return null;
        }
        const convertedColor = convertMT5Color(drawing.color);
        console.log(`✅ HLINE "${drawing.name}" at price ${drawing.price1} -> Y=${yCoord.y}, color=${convertedColor} (MT5: ${drawing.color})`);
        
        const result = {
          type: 'line',
          points: [0, yCoord.y, width, yCoord.y], // Full width - will be clipped by Konva
          color: convertedColor,
          strokeWidth: drawing.width,
          name: drawing.name,
          price: drawing.price1,
        };
        
        // DEBUG: Check for potential line duplication
        console.log(`🔴 CREATING HLINE: "${drawing.name}" at Y=${yCoord.y} with points=[0,${yCoord.y},${width},${yCoord.y}]`);
        
        return result;
      }

      // Handle TEXT objects - use EXACT coordinates from MT5
      if (drawing.type === 'TEXT') {
        console.log(`🎯 TEXT "${drawing.name}" - MT5 sent: time1=${drawing.time1} (${new Date(drawing.time1 * 1000).toISOString()}), price1=${drawing.price1}, text="${drawing.text}"`);
        
        const textCoord = getChartCoordinates(drawing.time1, drawing.price1);
        
        if (!textCoord) {
          console.log(`❌ TEXT "${drawing.name}" - coordinates unavailable for time=${drawing.time1} (${new Date(drawing.time1 * 1000).toISOString()}), price=${drawing.price1}`);
          console.log(`❌ This means getChartCoordinates is still failing even with extended time axis`);
          return null;
        }
        
        console.log(`✅ TEXT "${drawing.name}" - positioned at X=${textCoord.x}, Y=${textCoord.y}`);
        
        const textResult = {
          type: 'text',
          x: textCoord.x,
          y: textCoord.y,
          color: convertMT5Color(drawing.color),
          strokeWidth: drawing.width,
          text: drawing.text || '',
          name: drawing.name,
        };
        return textResult;
      }

      const startCoord = getChartCoordinates(drawing.time1, drawing.price1);
      
      if (!startCoord) {
        console.log(`❌ ${drawing.type} "${drawing.name}" filtered out - invalid coordinates (time1: ${drawing.time1}, price1: ${drawing.price1})`);
        return null;
      }

      switch (drawing.type) {
        case 'VLINE':
          return {
            type: 'line',
            points: [startCoord.x, 0, startCoord.x, height],
            color: convertMT5Color(drawing.color),
            strokeWidth: drawing.width,
          };

        case 'TRENDLINE':
          if (drawing.time2 && drawing.price2) {
            const endCoord = getChartCoordinates(drawing.time2, drawing.price2);
            if (endCoord) {
              console.log(`🔴 TRENDLINE DEBUG: "${drawing.name}"`);
              console.log(`   Start: time=${drawing.time1}, price=${drawing.price1} -> X=${startCoord.x}, Y=${startCoord.y}`);
              console.log(`   End: time=${drawing.time2}, price=${drawing.price2} -> X=${endCoord.x}, Y=${endCoord.y}`);
              console.log(`   Slope: ${(endCoord.y - startCoord.y) / (endCoord.x - startCoord.x)} (should be diagonal, not horizontal)`);
              
              const trendResult = {
                type: 'line',
                points: [startCoord.x, startCoord.y, endCoord.x, endCoord.y],
                color: convertMT5Color(drawing.color),
                strokeWidth: drawing.width,
                name: drawing.name,
              };
              
              if (Math.abs(startCoord.y - endCoord.y) < 2) {
                console.log(`⚠️ TRENDLINE APPEARS HORIZONTAL! Y difference: ${Math.abs(startCoord.y - endCoord.y)}px`);
              }
              
              return trendResult;
            } else {
              console.log(`❌ TRENDLINE "${drawing.name}" - end coordinate failed: time2=${drawing.time2}, price2=${drawing.price2}`);
            }
          } else {
            console.log(`❌ TRENDLINE "${drawing.name}" - missing time2/price2: time2=${drawing.time2}, price2=${drawing.price2}`);
          }
          break;

        case 'BOX':
          if (drawing.time2 && drawing.price2) {
            const endCoord = getChartCoordinates(drawing.time2, drawing.price2);
            if (endCoord) {
              const baseColor = convertMT5Color(drawing.color);
              // Make fill color semi-transparent like MT5
              const fillColor = drawing.fill ? `${baseColor}40` : undefined; // 40 = 25% opacity in hex
              console.log(`📦 BOX "${drawing.name}": strokeWidth set to 1 (was ${drawing.width}) for thin lines like MT5`);
              return {
                type: 'rect',
                x: Math.min(startCoord.x, endCoord.x),
                y: Math.min(startCoord.y, endCoord.y),
                width: Math.abs(endCoord.x - startCoord.x),
                height: Math.abs(endCoord.y - startCoord.y),
                color: baseColor,
                strokeWidth: 1, // Thin lines to match MT5 appearance
                fill: drawing.fill,
                fillColor: fillColor,
              };
            }
          }
          break;

        case 'ARROW':
          return {
            type: 'arrow',
            x: startCoord.x,
            y: startCoord.y,
            color: convertMT5Color(drawing.color),
            strokeWidth: drawing.width,
            price: drawing.price1,
            time: drawing.time1,
            name: drawing.name,
          };
          
      }
      
      return null;
    });
    
    const filtered = results.filter(Boolean);
    const textCount = filtered.filter(d => d && d.type === 'text').length;
    const lineCount = filtered.filter(d => d && d.type === 'line').length;
    const rectCount = filtered.filter(d => d && d.type === 'rect').length;
    const arrowCount = filtered.filter(d => d && d.type === 'arrow').length;
    console.log(`📊 Final: ${filtered.length}/${drawings?.drawings?.length || 0} drawings rendered`);
    console.log(`   - LINES: ${lineCount} (should be 3: 2 HLINEs + 1 TRENDLINE)`);
    console.log(`   - RECTS: ${rectCount} (should be 11: 1 HistoryRect + 10 Zones)`);
    console.log(`   - ARROWS: ${arrowCount} (should be 1: HistArrow)`);
    console.log(`   - TEXT: ${textCount} (should be 11: 1 HistLabel + 10 Zone labels)`);
    console.log(`🎯 SOLUTION: BOX strokeWidth set to 0 to eliminate ${rectCount-1} * 2 = ${(rectCount-1)*2} extra horizontal lines from zone borders`);
    
    // Debug individual line objects to identify duplicates
    filtered.filter(d => d && d.type === 'line').forEach((line, i) => {
      if (line && 'points' in line && 'name' in line && line.points && line.points.length > 1) {
        console.log(`   Line ${i}: name=${line.name}, color=${line.color}, Y=${line.points[1]} (price=${(line as any).price || 'N/A'})`);
      }
    });
    
    return filtered;
  }, [drawings?.drawings, getChartCoordinates, width, height]); // More specific dependency

  // DEBUG: Add a check to see if there are any rendering artifacts
  React.useEffect(() => {
    // Check ALL line elements in Konva canvas
    setTimeout(() => {
      const konvaLines = document.querySelectorAll('canvas');
      console.log(`🔍 Found ${konvaLines.length} canvas elements`);
      
      // Check SVG lines too
      const svgLines = document.querySelectorAll('line');
      console.log(`🔍 Found ${svgLines.length} SVG line elements`);
      
      svgLines.forEach((line, i) => {
        const x1 = line.getAttribute('x1');
        const y1 = line.getAttribute('y1'); 
        const x2 = line.getAttribute('x2');
        const y2 = line.getAttribute('y2');
        const stroke = line.getAttribute('stroke');
        console.log(`   SVG Line ${i}: (${x1},${y1}) to (${x2},${y2}) stroke=${stroke}`);
      });
      
      // Also check for any path elements that might be horizontal lines
      const paths = document.querySelectorAll('path');
      console.log(`🔍 Found ${paths.length} path elements`);
    }, 1000);
  });

  const handleMouseDown = useCallback((e: any) => {
    if (selectedTool === 'cursor') return;

    const pos = e.target.getStage().getPointerPosition();
    const newDrawing: LocalDrawing = {
      id: `local_${Date.now()}`,
      type: selectedTool,
      points: [pos.x, pos.y],
      color: '#2962ff',
      strokeWidth: 2,
      isFinished: false
    };

    setCurrentDrawing(newDrawing);
    setIsDrawing(true);
  }, [selectedTool]);

  const handleMouseMove = useCallback((e: any) => {
    if (!isDrawing || !currentDrawing) return;

    const pos = e.target.getStage().getPointerPosition();
    
    let newPoints: number[];
    
    switch (currentDrawing.type) {
      case 'horizontal_line':
        newPoints = [0, currentDrawing.points[1], width, currentDrawing.points[1]];
        break;
      case 'vertical_line':
        newPoints = [currentDrawing.points[0], 0, currentDrawing.points[0], height];
        break;
      case 'trend_line':
        newPoints = [...currentDrawing.points.slice(0, 2), pos.x, pos.y];
        break;
      case 'rectangle':
        newPoints = [
          currentDrawing.points[0],
          currentDrawing.points[1],
          pos.x - currentDrawing.points[0],
          pos.y - currentDrawing.points[1]
        ];
        break;
      default:
        newPoints = currentDrawing.points;
    }

    setCurrentDrawing({
      ...currentDrawing,
      points: newPoints
    });
  }, [isDrawing, currentDrawing, width, height]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !currentDrawing) return;

    const finishedDrawing = {
      ...currentDrawing,
      isFinished: true
    };

    setLocalDrawings(prev => [...prev, finishedDrawing]);
    setCurrentDrawing(null);
    setIsDrawing(false);
  }, [isDrawing, currentDrawing]);

  const renderDrawing = (drawing: any, index: number) => {
    const key = `drawing_${index}`;
    
    if (drawing.type === 'line') {
      return (
        <Line
          key={key}
          points={drawing.points}
          stroke={drawing.color}
          strokeWidth={drawing.strokeWidth}
          lineCap="round"
          lineJoin="round"
        />
      );
    }
    
    if (drawing.type === 'rect') {
      return (
        <Rect
          key={key}
          x={drawing.x}
          y={drawing.y}
          width={drawing.width}
          height={drawing.height}
          stroke={drawing.color}
          strokeWidth={0.5} // Very thin lines to match MT5
          fill={drawing.fillColor || "transparent"}
        />
      );
    }
    
    if (drawing.type === 'arrow') {
      const isHovered = hoveredArrow === key;
      
      return (
        <Group 
          key={key}
          onMouseEnter={() => setHoveredArrow(key)}
          onMouseLeave={() => setHoveredArrow(null)}
        >
          {/* Arrow body (vertical line) */}
          <Line
            points={[drawing.x, drawing.y + 15, drawing.x, drawing.y - 5]}
            stroke={drawing.color}
            strokeWidth={Math.max(2, drawing.strokeWidth)}
            lineCap="round"
          />
          {/* Arrow head (triangle) */}
          <Line
            points={[drawing.x - 6, drawing.y - 8, drawing.x, drawing.y - 15, drawing.x + 6, drawing.y - 8]}
            stroke={drawing.color}
            fill={drawing.color}
            strokeWidth={1}
            closed={true}
          />
          {/* Invisible hit area for mouse events */}
          <Rect
            x={drawing.x - 15}
            y={drawing.y - 25}
            width={30}
            height={40}
            fill="transparent"
          />
          {/* Tooltip */}
          {isHovered && (
            <Group>
              <Rect
                x={drawing.x + 15}
                y={drawing.y - 30}
                width={120}
                height={40}
                fill="rgba(0, 0, 0, 0.8)"
                cornerRadius={4}
              />
              <Text
                x={drawing.x + 20}
                y={drawing.y - 25}
                text={`${drawing.name || 'Arrow'}`}
                fontSize={12}
                fill="white"
                fontFamily="Arial"
              />
              <Text
                x={drawing.x + 20}
                y={drawing.y - 12}
                text={`Price: ${drawing.price?.toFixed(5) || 'N/A'}`}
                fontSize={10}
                fill="white"
                fontFamily="Arial"
              />
            </Group>
          )}
        </Group>
      );
    }
    
    if (drawing.type === 'text') {
      // Calculate text dimensions for centering (approximate)
      const fontSize = 12;
      const textWidth = drawing.text.length * fontSize * 0.6; // Rough approximation
      const textHeight = fontSize;
      
      return (
        <Text
          key={key}
          x={drawing.x}
          y={drawing.y}
          text={drawing.text}
          fontSize={fontSize}
          fill={drawing.color}
          fontFamily="Arial"
          offsetX={textWidth / 2}  // Center horizontally like MT5 ANCHOR_CENTER
          offsetY={textHeight / 2} // Center vertically like MT5 ANCHOR_CENTER
        />
      );
    }
    
    return null;
  };

  const renderLocalDrawing = (drawing: LocalDrawing) => {
    const key = `local_${drawing.id}`;
    
    switch (drawing.type) {
      case 'horizontal_line':
      case 'vertical_line':
      case 'trend_line':
        return (
          <Line
            key={key}
            points={drawing.points}
            stroke={drawing.color}
            strokeWidth={drawing.strokeWidth}
            lineCap="round"
            lineJoin="round"
            dash={drawing.isFinished ? [] : [5, 5]}
          />
        );
      
      case 'rectangle':
        if (drawing.points.length >= 4) {
          return (
            <Rect
              key={key}
              x={drawing.points[0]}
              y={drawing.points[1]}
              width={drawing.points[2]}
              height={drawing.points[3]}
              stroke={drawing.color}
              strokeWidth={drawing.strokeWidth}
              fill="transparent"
              dash={drawing.isFinished ? [] : [5, 5]}
            />
          );
        }
        break;
    }
    
    return null;
  };

  if (width === 0 || height === 0) return null;

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: selectedTool === 'cursor' ? 'none' : 'all',
      zIndex: 10, // Ensure drawings appear above chart content
      ...style // Apply passed style prop to override zIndex
    }}>
      <Stage
        ref={stageRef}
        width={width}
        height={height}
        onMouseDown={handleMouseDown}
        onMousemove={handleMouseMove}
        onMouseup={handleMouseUp}
      >
        <Layer
          clipFunc={(ctx) => {
            // Get precise price scale width from Lightweight Charts API
            const priceScaleWidth = getPriceScaleWidth();
            const clippingWidth = width - priceScaleWidth;
            console.log(`✂️ Clipping: total width ${width}px - price scale ${priceScaleWidth}px = plotting area ${clippingWidth}px`);
            ctx.rect(0, 0, clippingWidth, height);
          }}
        >
          {/* Server drawings */}
          {convertedDrawings.map((drawing, index) => renderDrawing(drawing, index))}
          
          {/* Local drawings */}
          {localDrawings.map(renderLocalDrawing)}
          
          {/* Debug: Log rendering counts */}
          {(() => {
            console.log(`🎨 Rendering: ${convertedDrawings.length} server + ${localDrawings.length} local = ${convertedDrawings.length + localDrawings.length} total drawings`);
            const localLines = localDrawings.filter(d => d.type === 'horizontal_line' || d.type === 'vertical_line' || d.type === 'trend_line');
            if (localLines.length > 0) {
              console.log(`⚠️ LOCAL LINES FOUND: ${localLines.length} local line drawings may be creating extra horizontal lines!`);
              localLines.forEach((line, i) => console.log(`   Local Line ${i}: type=${line.type}, color=${line.color}`));
            }
            return null;
          })()}
          
          {/* Current drawing */}
          {currentDrawing && renderLocalDrawing(currentDrawing)}
        </Layer>
      </Stage>
    </div>
  );
};

export default DrawingOverlay;